/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { JSX } from 'preact';
import { ForwardedRef, forwardRef } from 'preact/compat';
import { useImperativeHandle, useRef, useLayoutEffect } from 'preact/hooks';
import { ReorderableContext } from '../hooks/PRIVATE_useReorderable';
import { DataState } from '../UNSAFE_Collection';
import { HiddenAccessible } from '../UNSAFE_HiddenAccessible';
import { List } from '../PRIVATE_List';
import { ListProps, ListViewContextMenuConfig, ListViewHandle } from './List.types';
import {
  getScrollPosition,
  setScrollPosition,
  ListViewScrollPosition
} from './ScrollPositionUtils';
import { MemoizeListViewReorderableItem } from './ListViewReorderableItem';
import { useListViewReorder } from './useListViewReorder';
import { ListViewContextMenu } from './ListViewContextMenu';
import { useListViewContextMenu } from './useListViewContextMenu';

/**
 * Props specific to non-virtualized ListView
 */
type NonVirtualizedListProps<K, D> = {
  /**
   * The data that is used to render each item in ListView.  If the value is null, then ListView will show nothing
   * if hasMore is false, or loading indicator if hasMore is true.
   */
  data: D[] | null;
  /**
   * A callback function to extract the key which is used to identify the item.
   */
  getRowKey: (data: D) => K;
  /**
   * Whether there are more data available to load.  By default this returns false.
   */
  hasMore?: boolean;
  /**
   * An optional callback function which is invoked when user scrolls to the end of the list.
   */
  onLoadMore?: () => void;

  /**
   * Configuration used to specify a context menu.
   */
  contextMenuConfig?: ListViewContextMenuConfig<K, D>;
};

/**
 * Props for the ListView Component
 */
export type Props<K, D> = Pick<
  ListProps<K, D>,
  | 'aria-label'
  | 'aria-labelledby'
  | 'children'
  | 'currentKey'
  | 'currentItemVariant'
  | 'gridlines'
  | 'onCurrentKeyChange'
  | 'onItemAction'
  | 'onSelectionChange'
  | 'promotedSection'
  | 'selectedKeys'
  | 'selectionMode'
  | 'viewportConfig'
  | 'testId'
  | 'scrollPositionOverride'
  | 'onReorder'
> &
  NonVirtualizedListProps<K, D>;
/**
 * A list view displays data items as a list or a grid with highly interactive features.
 */
export const ListView = forwardRef(
  <K extends string | number, D>(props: Props<K, D>, ref?: ForwardedRef<ListViewHandle<K>>) => {
    const { currentKey, contextMenuConfig, onCurrentKeyChange } = props;
    const scrollPositionRef = useRef<ListViewScrollPosition<K>>();
    const scrollListRef = useRef<HTMLDivElement | null>(null);
    const dataMetadata = props.data
      ? props.data.map((aData: D) => {
          const key = props.getRowKey(aData);
          return { data: aData, metadata: { key } };
        })
      : null;
    const loadRange = props.onLoadMore ? props.onLoadMore : () => {};
    const dataState = dataMetadata
      ? ({
          offset: 0,
          data: dataMetadata,
          sizePrecision: props.hasMore ? 'atLeast' : 'exact',
          totalSize: dataMetadata.length
        } as DataState<K, D>)
      : null;

    useImperativeHandle(ref!, () => ({
      getScrollPosition: () => {
        if (scrollListRef.current) {
          return getScrollPosition(scrollListRef.current);
        }
        return { y: 0 };
      }
    }));

    // update scroll position if a new value is provided
    useLayoutEffect(() => {
      if (scrollListRef.current && scrollPositionRef.current !== props.scrollPositionOverride) {
        setScrollPosition(scrollListRef.current, props.scrollPositionOverride);
        scrollPositionRef.current = props.scrollPositionOverride;
      }
    }, [props.scrollPositionOverride]);

    /**
     * Reorder Section
     */
    const isReorderEnabled = props.onReorder != undefined;
    const {
      reorderProps,
      reorderItemProps,
      reorderContext,
      reorderInstructionsId,
      reorderInstructions
    } = useListViewReorder({
      currentKey: props.currentKey,
      onReorder: props.onReorder,
      rootRef: scrollListRef,
      viewportConfig: props.viewportConfig
    });

    const { triggerProps, menuProps, contextMenuContext } = useListViewContextMenu(
      dataState,
      contextMenuConfig,
      currentKey,
      onCurrentKeyChange,
      scrollListRef
    );

    if (isReorderEnabled && reorderItemProps) {
      return (
        <>
          <ReorderableContext.Provider value={reorderContext}>
            <List
              {...props}
              contextMenuTriggerProps={triggerProps}
              reorderProps={reorderProps}
              onLoadRange={loadRange}
              scrollerRef={scrollListRef}
              data={dataState}>
              {(context) => (
                <MemoizeListViewReorderableItem
                  {...reorderItemProps}
                  itemKey={context.metadata.key}>
                  {props.children(context)}
                </MemoizeListViewReorderableItem>
              )}
            </List>
            <span id={reorderInstructionsId}>
              <HiddenAccessible>{reorderInstructions}</HiddenAccessible>
            </span>
          </ReorderableContext.Provider>
          {contextMenuConfig && (
            <ListViewContextMenu
              {...menuProps}
              contextMenuConfig={contextMenuConfig}
              contextMenuContext={contextMenuContext}
            />
          )}
        </>
      );
    }

    return (
      <>
        <List
          {...props}
          onLoadRange={loadRange}
          scrollerRef={scrollListRef}
          data={dataState}
          contextMenuTriggerProps={triggerProps}>
          {props.children}
        </List>
        {contextMenuConfig && (
          <ListViewContextMenu
            {...menuProps}
            contextMenuConfig={contextMenuConfig}
            contextMenuContext={contextMenuContext}
          />
        )}
      </>
    );
  }
) as <K extends string | number, D>(
  props: Props<K, D> & { ref?: ForwardedRef<ListViewHandle<K>> }
) => JSX.Element;
